import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { ReactElement, useCallback, useMemo } from 'react';

import { useSDK } from '../../../app';
import { isMobile } from '../../../utils/isMobile';
import { AudioIcon } from './audioIcon/default';
import { SlashedAudioIcon } from './audioIcon/slashed';
import { PhoneIcon } from './phoneIcon';
import { FullscreenIcon } from './resizeIcon/fullscreen';
import { NormalscreenIcon } from './resizeIcon/normalscreen';
import { SwitchCameraIcon } from './switchCameraIcon';
import { VideoIcon } from './videoIcon/default';
import { SlashedVideoIcon } from './videoIcon/slashed';

const styles = css`
	align-items: center;
	display: flex;
	justify-content: space-around;
	max-width: 80%;
	width: 280px;

	& > * {
		border-radius: 1.5em;
		color: #fff;
		cursor: pointer;
		padding: 0.75em;
	}

	& > .normalscreen,
	& > .fullscreen,
	& > .switch,
	& > .audio,
	& > .video {
		align-items: center;
		border-radius: 30px;
		cursor: pointer;
		display: flex;
		justify-content: center;
		padding: 12px;
	}
`;

export const ControlButtons = styled(
	observer(
		({
			className,
			fullScreen,
			setFullScreen,
			hideLocalVideo,
		}: {
			hideLocalVideo: VoidFunction;
			className: string;
			fullScreen: boolean;
			setFullScreen: (v: boolean) => void;
		}): ReactElement => {
			const { videoCallsStore } = useSDK();
			const onAudioIconClick = useMemo(
				() => (videoCallsStore.audioMuted ? videoCallsStore.resumeAudio : videoCallsStore.muteAudio),
				[videoCallsStore.audioMuted, videoCallsStore.resumeAudio, videoCallsStore.muteAudio]
			);
			const onVideoIconClick = useMemo(
				() => (videoCallsStore.videoMuted ? videoCallsStore.resumeVideo : videoCallsStore.muteVideo),
				[videoCallsStore.videoMuted, videoCallsStore.resumeVideo, videoCallsStore.muteVideo]
			);
			return (
				<div className={className}>
					{!isMobile &&
						(fullScreen ? (
							<NormalscreenIcon className="normalscreen" onClick={(): void => setFullScreen(false)} />
						) : (
							<FullscreenIcon className="fullscreen" onClick={(): void => setFullScreen(true)} />
						))}
					{isMobile && (
						<SwitchCameraIcon
							onClick={(): Promise<void> => (hideLocalVideo(), videoCallsStore.switchCamera())}
							className="switch"
						/>
					)}
					{videoCallsStore.videoMuted ? (
						<SlashedVideoIcon onClick={onVideoIconClick} className="video" />
					) : (
						<VideoIcon onClick={onVideoIconClick} className="video" />
					)}
					<PhoneIcon
						onClick={useCallback(
							() => (videoCallsStore.reject(), void videoCallsStore.stop()),
							[videoCallsStore]
						)}
						className="phone"
					/>
					{videoCallsStore.audioMuted ? (
						<SlashedAudioIcon onClick={onAudioIconClick} className="audio" />
					) : (
						<AudioIcon onClick={onAudioIconClick} className="audio" />
					)}
				</div>
			);
		}
	)
)(styles);
